
import { Component, Vue, Watch, Prop } from "vue-property-decorator"
import { namespace, Getter, Mutation, Action } from "vuex-class"

import moment from "moment"
import { Insight, DataType, InsightCalculation, LabelFormat, FormattingRule, InsightCalculationTotal, CalculationType } from "@/types/insights"

import { get } from "lodash"

// @ts-ignore
import VuePerfectScrollbar from "vue-perfect-scrollbar"

import { cellFormatting } from "@/mixins/cellFormatting"
import { DashboardState } from "@/types/dashboard"
import { UserStatus } from "@/types/userStatus"
import api from "@/services/api"
import User, { CurrentUser, UserIdWithStatus, UserIdWithStatusAndFields, LabeledValue } from "@/types/user"
import vSelect from "vue-select"
import { formatInsight } from "@/mixins/FormatInsight"
import { DataDefinitionWithFields } from "@/types"
import { formatFieldsMixin } from "@/mixins/formatFieldsMixin"
import UserHeaderFields from "../CaseDetails/Users/UserHeaderFields.vue"

@Component({
	components: {
		"scrollbar": VuePerfectScrollbar,
		"v-select": vSelect,
		"user-header-fields": UserHeaderFields
	}
})
export default class CalculationTable extends Vue {
	@Prop({ default: () => [] })
	resultGroups!: any[];

	@Prop({ default: null })
	insight!: Insight

	@Prop({ default: () => [] })
	calculations!: InsightCalculation[]

	@Prop({ default: () => [] })
	calculationTotals!: InsightCalculationTotal[]

	@Prop({ default: LabelFormat.Text })
	labelFormat!: LabelFormat;

	@Prop({ default: false })
	clickableRows!: Boolean

	@Prop({ default: () => { return { calculationTableClickedRows: {} } } })
	dashboardState!: DashboardState;

	@Getter("getAvailableUserStatuses") public availableUserStatuses!: UserStatus[];

	@Getter("getMe") public me!: CurrentUser;

	@Action("setCurrentUserStatus")
	private setCurrentUserStatus!: (userStatus: UserStatus) => void;

	@Action("clearCurrentUserStatus")
	private clearCurrentUserStatus!: () => void;

	private get availableUserStatusesWithNotSet () : UserStatus[] {
		const userStatuses = this.availableUserStatuses.slice(0) // need to duplicate the array and apparently this is fastest
		userStatuses.push({
			id: null,
			title: "Not Set"
		})
		return userStatuses
	}
	reloadResultsKey: string | null = null;

	userDataByUserId: { [userId:string] : UserIdWithStatusAndFields } = {}
	isMounted = false
	userDataDefinition?: DataDefinitionWithFields

	async mounted () {
		await this.bulkLoadUserDataIfNecessary()
		this.isMounted = true
	}

	/**
	 * Maps calculations to columns based on grouping labels
	 */
	private get calculationsColumns () {
		return this.calculations.map((c: InsightCalculation, index: number) => {
			if (c.groupBy && c.groupByKeyLabels) {
				// "Group by" calculations can have multiple columns per group by label
				return Object.keys(c.groupByKeyLabels)
					.map((l: string) => {
						return {
							"label": c.groupByKeyLabels![l],
							"key": "calculations[" + c.key + "]." + l,
							"calculationKey": c.key,
							"formattingRules": c.formattingRules ? c.formattingRules : [],
							"fieldDataType": DataType.number
						}
					})
			} else {
				// Single calculation
				return [
					{
						"key": "calculations[" + c.key + "]",
						"calculationKey": c.key,
						"label": c.label,
						"formattingRules": c.formattingRules ? c.formattingRules : [],
						"fieldDataType": DataType.number
					}
				]
			}
		}).flat()
	}

	private get calculationTotalsColumns () {
		if (!this.insight || !this.insight.calculationTotals) return []

		return this.insight.calculationTotals.map((total, index) => {
			if (total.groupBy && total.groupBy !== "" && !total.collate) {
				const totalledCalculations = total.calculationList
					.map(calcKey => this.calculations.find(c => c.key === calcKey))
					.filter(calc => !!calc)

				const uniqueGroupLabels: { [key: string]: string } = totalledCalculations.reduce((acc: { [key: string]: string }, calc) => {
					if (calc && calc.groupByKeyLabels) {
						Object.keys(calc!.groupByKeyLabels as any).forEach(calcGroupByKey => {
							if (!acc[calcGroupByKey]) {
								acc[calcGroupByKey] = calc!.groupByKeyLabels![calcGroupByKey]
							}
						})
					}

					return acc
				}, {})

				return Object.keys(uniqueGroupLabels)
					.map((key: string) => {
						return {
							"label": uniqueGroupLabels[key],
							"key": "calculationTotals[" + index + "]." + key,
							"calculationKey": total.key || index.toString(),
							"formattingRules": total.formattingRules ? total.formattingRules : [],
							"fieldDataType": DataType.number
						}
					})
			} else {
				return [{
					"key": "calculationTotals[" + index + "]",
					"calculationKey": total.key || index.toString(),
					"label": total.title,
					"formattingRules": total.formattingRules ? total.formattingRules : [],
					"fieldDataType": DataType.number
				}]
			}
		}).flat()
	}

	public get labelColumns () {
		var columns = [
			{
				key: "label",
				calculationKey: "",
				label: "",
				formattingRules: [],
				value: null
			}
		]

		if (this.labelFormat === LabelFormat.WeekRange) {
			const optionalColumns = [
				{
					key: "weekStart",
					calculationKey: "",
					label: "Week Start",
					formattingRules: [],
					value: null
				},
				{
					key: "weekEnd",
					calculationKey: "",
					label: "Week End",
					formattingRules: [],
					value: null
				}
			]

			columns.push(...optionalColumns)
		}

		return columns
	}

	public get optionalPostColumns () {
		const optionalColumns = []
		if (this.showUserStatus()) {
			optionalColumns.push({
				key: "userStatus",
				label: "User Status",
				formattingRules: []
			})
		}

		return optionalColumns
	}

	public get numericalColumns () {
		const sort = this.insight ? this.insight.calculationSort || [] : []

		const calculationsColumns = this.calculationsColumns
		const calculationTotalsColumns = this.calculationTotalsColumns

		const sortedColumns = sort.map(key => {
			const calcColumns = calculationsColumns.filter(c => c.calculationKey === key)
			if (calcColumns.length > 0) return calcColumns

			return calculationTotalsColumns.filter(c => c.calculationKey === key)
		}).flat()

		return [
			...sortedColumns,
			...calculationsColumns.filter(c => !sort.includes(c.calculationKey)),
			...calculationTotalsColumns.filter(c => !sort.includes(c.calculationKey)),
			{
				key: "value",
				label: "value",
				calculationKey: "",
				formattingRules: []
			}
		]
	}

	private showUserStatus () : boolean {
		return this.showUserHeaderFields() &&
				!!this.insight.showCalculatedUserStatus
	}

	private showUserHeaderFields () : boolean {
		return this.insight.groupByDataType === DataType.userRole &&
				this.insight.groupByOption === "user"
	}

	public get displayNumericalColumns () {
		return this.numericalColumns.filter(c => c.key !== "value")
	}

	private async bulkLoadUserDataIfNecessary () : Promise<void> {
		if (this.showUserHeaderFields()) {
			this.userDataDefinition = await api.userSpecialDefinition()

			const userIds = this.resultGroups.filter(c => !!c.value).map(c => c.value)
			const userIdsWithData = await api.userStatusListWithFieldsGet(userIds)
			userIdsWithData.forEach(userIdWithData => {
				this.userDataByUserId[userIdWithData.userId] = userIdWithData
			})
		}
	}

	public get items (): { [key: string]: any }[] {
		const rows = this.resultGroups.map((resultGroup: any) => {
			const row: {[key: string]: any} = this.numericalColumns.reduce((values: { [key: string]: any }, column) => {
				values[column.key] = get(resultGroup, column.key)
				if (values[column.key] === undefined) {
					values[column.key] = this.getUndefinedColumnValue(column)
				}

				if (column.key !== "value") { // don't round the resultGroup.value
					values[column.key] = formatInsight.methods.roundNumber(values[column.key], 2)
				}

				return values
			}, {})

			row["label"] = resultGroup.label

			if (this.showUserHeaderFields()) {
				const userId = resultGroup["value"]

				const userData = this.userDataByUserId[userId]

				if (userData && userData.userHeaderFieldValues) {
					row["userHeaderFieldValues"] = userData.userHeaderFieldValues
				} else {
					row["userHeaderFieldValues"] = {}
				}
			}

			if (this.showUserStatus()) {
				const userId = resultGroup["value"]
				row["userStatus"] = this.userDataByUserId[userId]
				this.setUserStatusRowColumn(row, userId)
			}
			return row
		})

		return rows
	}

	private getUndefinedColumnValue (column: any) {
		var calculation: InsightCalculation | InsightCalculationTotal | null = null
		var defaultExpression = ""

		// Find the matching Calculation or CalculationTotal
		if (column.key.includes("calculations")) {
			calculation = this.calculations.find(c => c.key === column.calculationKey)!
			defaultExpression = calculation ? "{" + calculation!.label + "}" : ""
		} else if (column.key.includes("calculationTotals")) {
			calculation = this.calculationTotals[column.calculationKey]
			defaultExpression = calculation ? "{" + calculation!.title + "}" : ""
		}

		// If the calculation uses an expression, return None as the expression could not be performed
		if (calculation && calculation.expression && defaultExpression && calculation.expression.replace(defaultExpression, "")) {
			return "None"
		}
		return 0
	}

	private setUserStatusRowColumn (row: { [key:string]: any }, userId: string) {
		const userStatusMapping = this.userDataByUserId[userId]

		if (!userStatusMapping) {
			row["userStatus"] = "Not Set"
			return
		}

		if (userStatusMapping.isExternal) {
			row["userStatus"] = "External User"
			return
		}

		row["userStatus"] = userStatusMapping.userStatus || "Not Set"
	}

	/**
	 * Adds up the total value of each row
	 */
	private get TotalsRow () {
		var totals: { [key: string]: any } = {
			label: "Total",
			hideFormatting: true
		}

		// For each column loop through the row and add up the values
		this.numericalColumns.forEach((column:any) => {
			if (column.key !== "label" && column.key !== "value") {
				let columnCalculationResult = this.getColumnCalculationResult(column)
				totals[column.key] = formatInsight.methods.roundNumber(columnCalculationResult, 1)
			}
		})

		return [totals]
	}

	private getColumnCalculationResult (column: any) : number {
		let columnCalculationResult = 0
		const calculationDetails = this.calculations.find(c => c.key === column.calculationKey)

		let calculationType = CalculationType.Total
		if (calculationDetails &&
			calculationDetails.calcType !== null &&
			calculationDetails.calcType !== undefined) {
			calculationType = calculationDetails.calcType
		}

		let firstItem = true
		this.items.forEach((row:any) => {
			var value = typeof row[column.key] === "string" ? Number.parseFloat(row[column.key]) : row[column.key]

			if (typeof value === "number") {
				switch (calculationType) {
					case CalculationType.Min:
						if (firstItem) {
							columnCalculationResult = value
						} else {
							columnCalculationResult = Math.min(columnCalculationResult, value)
						}
						break
					case CalculationType.Max:
						if (firstItem) {
							columnCalculationResult = value
						} else {
							columnCalculationResult = Math.max(columnCalculationResult, value)
						}
						break
					default:
						columnCalculationResult += value
				}
				firstItem = false
			}
		})

		switch (calculationType) {
			case CalculationType.Average:
				if (this.items.length > 0) {
					columnCalculationResult = columnCalculationResult / this.items.length
				} else {
					columnCalculationResult = 0
				}
				break
			default:
				break
		}

		return columnCalculationResult
	}

	/**
	 * Returns a list of all rows and the totals row
	 */
	public get allItems () {
		return this.items.concat(this.TotalsRow)
	}

	private cellStyle (key: string, value: any, field: any, row: any) {
		if (!field || !field.formattingRules || row.hideFormatting) return null

		return cellFormatting.cellStyle(field.formattingRules, value, field)
	}

	private cellBarStyle (row: { [key: string]: any }, cell: any, column: any) {
		return cellFormatting.cellBarStyle(column.formattingRules, this.allItems, row, cell, column)
	}

	private icon (key: string, value: any, field: any) {
		if (!field || !field.formattingRules) return null

		return cellFormatting.icon(field.formattingRules, value, field)
	}

	public rowClick (row: any) {
		if (!row) {
			return
		}
		this.$emit("rowClick", row)
	}

	private rowWasClicked (row: any) : boolean {
		if (!this.insight || !this.insight.id) {
			return false
		}
		const filterFromClickedRow = this.dashboardState.calculationTableClickedRows[this.insight.id]
		if (!filterFromClickedRow) {
			return false
		}

		if (filterFromClickedRow.value === row.value) {
			return true
		}
		return false
	}

	private async onUserStatusChanged (userId: string, userStatus: UserStatus) {
		if (userId === this.me.id) {
			if (userStatus.id) {
				await this.setCurrentUserStatus(userStatus)
			} else {
				await this.clearCurrentUserStatus()
			}
			await this.bulkLoadUserDataIfNecessary()
			return
		}

		if (userStatus.id) {
			await api.setUserStatus(userId, userStatus.id!)
		} else {
			await api.clearUserStatus(userId)
		}
		await this.bulkLoadUserDataIfNecessary()
	}

	public get userHasSetStatusPermission () : boolean {
		return !!(this.me &&
			this.me.extendedPermissions &&
			this.me.extendedPermissions["setOtherUsersStatus"])
	}

	private userStatusSelectOpen (userId: string) {
		this.userIdStatusSelectOpen = userId
	}

	private userStatusSelectClose () {
		this.userIdStatusSelectOpen = ""
	}

	userIdStatusSelectOpen: string = ""

	private canClickRow (row: any) : boolean {
		if (!this.clickableRows) {
			return false
		}

		if (row["userStatus"] === "External User") {
			return false
		}

		return true
	}

	private getLabeledValueDisplayLabel (fieldId: string, labeledValue: LabeledValue): string {
		const userField = this.userDataDefinition!.fields.find(c => c.field.id === fieldId)

		if (!userField) {
			return ""
		}
		const label = formatFieldsMixin.methods.getDisplayLabelFromLabeledValue(labeledValue, userField.field.dataType!)
		return label
	}

	private sanitizeFieldTitle (title: string) : string {
		const result = title.replace(/[^a-zA-Z0-9]/g, "")
		return result
	}
}

